import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Mail, ArrowRight, Download } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { jsPDF } from 'jspdf';
import { db, functions } from '../firebase';

const countryNames = {
  HU: 'Magyarország',
  AT: 'Ausztria',
  SK: 'Szlovákia',
  RO: 'Románia',
  HR: 'Horvátország',
};

const getShippingLabel = (shippingMethod) => {
  if (shippingMethod === 'personal_pickup_omega') return 'Személyes átvétel (Omega Sportcentrum):';
  if (shippingMethod === 'gls_home') return 'GLS házhozszállítás:';
  if (shippingMethod === 'gls_parcel_locker') return 'GLS csomagautomata:';
  if (shippingMethod === 'packeta_parcel_locker') return 'Packeta csomagautomata:';
  if (shippingMethod === 'express') return 'GLS csomagautomata:';
  return 'GLS házhozszállítás:';
};

const formatPickupPointLabel = (pickupPoint) => {
  if (!pickupPoint || typeof pickupPoint !== 'object') return '';
  const id = typeof pickupPoint.id === 'string' ? pickupPoint.id.trim() : '';
  const name = typeof pickupPoint.name === 'string' ? pickupPoint.name.trim() : '';
  if (name && id) return `${name} (${id})`;
  return name || id;
};

const PDF_FONT_FAMILY = 'NotoSans';
const PDF_REGULAR_FONT_FILE = 'NotoSans-Regular.ttf';
const PDF_BOLD_FONT_FILE = 'NotoSans-Bold.ttf';

let pdfFontDataPromise;

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

const loadPdfFontData = async () => {
  if (!pdfFontDataPromise) {
    pdfFontDataPromise = Promise.all([
      fetch(`/fonts/${PDF_REGULAR_FONT_FILE}`),
      fetch(`/fonts/${PDF_BOLD_FONT_FILE}`),
    ]).then(async ([regularResponse, boldResponse]) => {
      if (!regularResponse.ok || !boldResponse.ok) {
        throw new Error('Nem sikerült betölteni a PDF betűkészletet.');
      }

      const [regularBuffer, boldBuffer] = await Promise.all([
        regularResponse.arrayBuffer(),
        boldResponse.arrayBuffer(),
      ]);

      return {
        regularBase64: arrayBufferToBase64(regularBuffer),
        boldBase64: arrayBufferToBase64(boldBuffer),
      };
    });
  }

  return pdfFontDataPromise;
};

const registerPdfFonts = async (doc) => {
  const { regularBase64, boldBase64 } = await loadPdfFontData();
  doc.addFileToVFS(PDF_REGULAR_FONT_FILE, regularBase64);
  doc.addFileToVFS(PDF_BOLD_FONT_FILE, boldBase64);
  doc.addFont(PDF_REGULAR_FONT_FILE, PDF_FONT_FAMILY, 'normal');
  doc.addFont(PDF_BOLD_FONT_FILE, PDF_FONT_FAMILY, 'bold');
};

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('order') || 'LV-XXXXX';
  const orderId = searchParams.get('id') || '';
  const invoiceToken = searchParams.get('token') || '';
  const [order, setOrder] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState('processing');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [isInvoiceDownloading, setIsInvoiceDownloading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadOrder = async () => {
      try {
        const q = query(collection(db, 'orders'), where('orderNumber', '==', orderNumber));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          setOrder(snapshot.docs[0].data());
          return;
        }
      } catch {
        // fallback to localStorage
      }

      const orders = JSON.parse(localStorage.getItem('lumenverde-orders') || '[]');
      const foundOrder = orders.find((o) => o.orderNumber === orderNumber);
      if (foundOrder) {
        setOrder(foundOrder);
      }
    };

    loadOrder();
  }, [orderNumber]);

  useEffect(() => {
    if (!orderId || !invoiceToken) return undefined;
    let active = true;
    let timer;
    const getCustomerInvoice = httpsCallable(functions, 'getCustomerInvoice');

    const refreshInvoice = async () => {
      try {
        const result = await getCustomerInvoice({ orderId, token: invoiceToken });
        if (!active) return;
        const status = result.data?.status || 'processing';
        setInvoiceStatus(status);
        setInvoiceNumber(result.data?.number || '');
        setInvoiceError(result.data?.error || '');
        if (status === 'processing') {
          timer = window.setTimeout(refreshInvoice, 2500);
        }
      } catch {
        if (active) setInvoiceStatus('unavailable');
      }
    };

    refreshInvoice();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [invoiceToken, orderId]);

  const downloadInvoice = async () => {
    if (!orderId || !invoiceToken || invoiceStatus !== 'issued') return;
    const popup = window.open('', '_blank');
    setIsInvoiceDownloading(true);
    try {
      const getCustomerInvoice = httpsCallable(functions, 'getCustomerInvoice');
      const result = await getCustomerInvoice({
        orderId,
        token: invoiceToken,
        download: true,
      });
      if (!result.data?.url) throw new Error('Missing invoice URL');
      if (popup) popup.location.href = result.data.url;
      else window.location.href = result.data.url;
    } catch {
      if (popup) popup.close();
      setInvoiceStatus('unavailable');
    } finally {
      setIsInvoiceDownloading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const generatePDF = async () => {
    if (!order) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const rightMargin = 20;
    const summaryLabelX = 120;
    const summaryValueX = pageWidth - rightMargin;
    const summaryLabelWidth = 42;
    const summaryValueWidth = 26;
    const summaryLineHeight = 6;
    let y = 20;
    let fontFamily = 'helvetica';

    try {
      await registerPdfFonts(doc);
      fontFamily = PDF_FONT_FAMILY;
    } catch (error) {
      console.error('Nem sikerült betölteni a PDF betűkészletet:', error);
    }

    const setFont = (style = 'normal') => {
      doc.setFont(fontFamily, style);
    };

    const formatPriceForPdf = (value) => formatPrice(value).replace(/\s/g, ' ');

    const drawSummaryLine = (label, value, { bold = false } = {}) => {
      setFont(bold ? 'bold' : 'normal');
      const labelLines = doc.splitTextToSize(label, summaryLabelWidth);
      const valueLines = doc.splitTextToSize(value, summaryValueWidth);
      const lineCount = Math.max(labelLines.length, valueLines.length);

      for (let i = 0; i < lineCount; i += 1) {
        const lineY = y + (i * summaryLineHeight);
        if (labelLines[i]) {
          doc.text(labelLines[i], summaryLabelX, lineY);
        }
        if (valueLines[i]) {
          doc.text(valueLines[i], summaryValueX, lineY, { align: 'right' });
        }
      }

      y += (lineCount * summaryLineHeight) + 1;
    };

    doc.setFontSize(24);
    setFont('bold');
    doc.text('Lumen Verde', pageWidth / 2, y, { align: 'center' });
    y += 10;

    doc.setFontSize(12);
    setFont('normal');
    doc.text('Rendelés visszaigazolás', pageWidth / 2, y, { align: 'center' });
    y += 20;

    doc.setFontSize(11);
    setFont('bold');
    doc.text(`Rendelési szám: #${order.orderNumber}`, 20, y);
    y += 7;
    setFont('normal');
    doc.text(`Dátum: ${formatDate(order.createdAt)}`, 20, y);
    y += 15;

    setFont('bold');
    doc.text('Megrendelő adatai:', 20, y);
    y += 7;
    setFont('normal');
    doc.text(`Név: ${order.customer.name}`, 20, y);
    y += 6;
    doc.text(`Email: ${order.customer.email}`, 20, y);
    y += 6;
    if (order.customer.phone) {
      doc.text(`Telefon: ${order.customer.phone}`, 20, y);
      y += 6;
    }
    y += 10;

    setFont('bold');
    doc.text('Szállítási cím:', 20, y);
    y += 7;
    setFont('normal');
    doc.text(`${order.customer.address}`, 20, y);
    y += 6;
    doc.text(`${order.customer.zip} ${order.customer.city}`, 20, y);
    y += 6;
    doc.text(`${countryNames[order.customer.country] || order.customer.country}`, 20, y);
    y += 10;

    if (order.billing) {
      setFont('bold');
      doc.text('Számlázási cím:', 20, y);
      y += 7;
      setFont('normal');
      doc.text(`${order.billing.address}`, 20, y);
      y += 6;
      doc.text(`${order.billing.zip} ${order.billing.city}`, 20, y);
      y += 6;
      doc.text(`${countryNames[order.billing.country] || order.billing.country}`, 20, y);
      y += 10;
    }

    y += 5;

    setFont('bold');
    doc.text('Termékek:', 20, y);
    y += 10;

    doc.setFillColor(240, 240, 240);
    doc.rect(20, y - 5, pageWidth - 40, 8, 'F');
    doc.text('Termék', 22, y);
    doc.text('Db', 120, y);
    doc.text('Egységár', 140, y);
    doc.text('Összesen', 170, y);
    y += 10;

    setFont('normal');
    order.items.forEach((item) => {
      const itemTotal = item.price * item.quantity;
      doc.text(item.name.substring(0, 35), 22, y);
      doc.text(item.quantity.toString(), 120, y);
      doc.text(formatPriceForPdf(item.price), 140, y);
      doc.text(formatPriceForPdf(itemTotal), 170, y);
      y += 8;
    });

    y += 5;
    doc.line(20, y, pageWidth - 20, y);
    y += 8;

    drawSummaryLine('Részösszeg:', formatPriceForPdf(order.subtotal));

    const shippingLabel = getShippingLabel(order.shipping);
    drawSummaryLine(
      shippingLabel,
      order.shippingCost === 0 ? 'Ingyenes' : formatPriceForPdf(order.shippingCost)
    );

    if (
      (order.shipping === 'gls_parcel_locker' || order.shipping === 'packeta_parcel_locker')
      && order.pickupPoint
    ) {
      const pickupPointLabel = formatPickupPointLabel(order.pickupPoint);
      if (pickupPointLabel) {
        setFont('normal');
        doc.text(`Automata: ${pickupPointLabel}`.substring(0, 85), 20, y);
        y += 8;
      }
    }

    drawSummaryLine('Fizetendő összeg:', formatPriceForPdf(order.total), { bold: true });
    y += 8;

    setFont('normal');
    const paymentText = order.payment === 'cod' ? 'Utánvéttel' : 'Bankkártyás fizetés';
    doc.text(`Fizetési mód: ${paymentText}`, 20, y);
    y += 20;

    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text('Köszönjük a vásárlásodat!', pageWidth / 2, y, { align: 'center' });
    y += 6;
    doc.text('www.lumenverde.hu', pageWidth / 2, y, { align: 'center' });

    doc.save(`rendeles-${order.orderNumber}.pdf`);
  };

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-8 animate-scale-in">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>

            <h1 className="font-display text-3xl md:text-4xl font-bold mb-4 animate-fade-up">
              Köszönjük a rendelésed!
            </h1>
            <p
              className="text-lg text-muted-foreground mb-2 animate-fade-up"
              style={{ animationDelay: '0.1s' }}
            >
              A rendelésed sikeresen rögzítettük.
            </p>
            <p
              className="font-display font-semibold text-xl mb-8 animate-fade-up"
              style={{ animationDelay: '0.15s' }}
            >
              Rendelésszám: #{orderNumber}
            </p>

            {order && (
              <div className="mb-8 flex flex-wrap justify-center gap-3 animate-fade-up" style={{ animationDelay: '0.18s' }}>
                <button onClick={generatePDF} className="btn-primary inline-flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Rendelés letöltése PDF-ben
                </button>
                {orderId && invoiceToken ? (
                  <button
                    type="button"
                    onClick={downloadInvoice}
                    disabled={invoiceStatus !== 'issued' || isInvoiceDownloading}
                    className="btn-secondary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
                    title={invoiceStatus === 'failed' ? invoiceError : undefined}
                  >
                    <Download className="w-4 h-4" />
                    {isInvoiceDownloading
                      ? 'Számla megnyitása…'
                      : invoiceStatus === 'issued'
                        ? `Számla letöltése${invoiceNumber ? ` (${invoiceNumber})` : ''}`
                        : invoiceStatus === 'failed'
                          ? 'A számla nem készült el'
                          : invoiceStatus === 'unavailable'
                            ? 'A számla nem elérhető'
                            : 'Számla készül…'}
                  </button>
                ) : null}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mb-12 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="card-zinga text-left">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold mb-1">Visszaigazoló email</h3>
                    <p className="text-sm text-muted-foreground">
                      Küldtünk egy visszaigazoló emailt a rendelésed részleteivel.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card-zinga text-left">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold mb-1">Szállítási értesítések</h3>
                    <p className="text-sm text-muted-foreground">
                      Emailben értesítünk a rendelésed állapotáról.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-zinga text-left mb-12 animate-fade-up" style={{ animationDelay: '0.25s' }}>
              <h2 className="font-display text-xl font-semibold mb-4">Mi történik ezután?</h2>
              <ol className="space-y-4">
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    1
                  </span>
                  <div>
                    <p className="font-medium">Rendelés feldolgozása</p>
                    <p className="text-sm text-muted-foreground">
                      Előkészítjük a friss termékeidet a szállításhoz.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    2
                  </span>
                  <div>
                    <p className="font-medium">Feladva</p>
                    <p className="text-sm text-muted-foreground">
                      A rendelésed úton van, a nyomkövetési adatokat emailben küldtük.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    3
                  </span>
                  <div>
                    <p className="font-medium">Kézbesítve</p>
                    <p className="text-sm text-muted-foreground">
                      Élvezd a Lumen Verde termékeidet! Ne felejtsd el hűtőben tárolni.
                    </p>
                  </div>
                </li>
              </ol>
            </div>

            <div
              className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up"
              style={{ animationDelay: '0.3s' }}
            >
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
                Vásárlás folytatása
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/" className="btn-secondary">
                Vissza a főoldalra
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
